import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DriverForm, toDriverPayload, type DriverFormData } from '@/components/drivers/DriverForm';

import type { Driver } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/drivers/new')({ component: DriverNewPage });

function DriverNewPage() {
  const nav = useNavigate();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: DriverFormData) =>
      (await api.post<Driver>('/drivers', toDriverPayload(data, undefined))).data,
    onSuccess: (driver) => {
      void qc.invalidateQueries({ queryKey: ['drivers'] });
      toast.success('Conducteur créé avec succès');
      void nav({ to: '/drivers/$id', params: { id: driver.id } });
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de la création')),
  });

  return (
    <div className="p-6 space-y-4 max-w-2xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/drivers"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Link>
        </Button>
        <h1 className="text-2xl font-bold">Nouveau conducteur</h1>
      </div>

      <Card>
        <CardHeader><CardTitle>Informations du conducteur</CardTitle></CardHeader>
        <CardContent>
          <DriverForm
            submitLabel="Créer le conducteur"
            pending={mutation.isPending}
            onSubmit={(d) => mutation.mutate(d)}
            onCancel={() => void nav({ to: '/drivers' })}
          />
        </CardContent>
      </Card>
    </div>
  );
}
